import {ChangeDetectionStrategy, Component, InputSignal, inject, input} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {Observable, of} from 'rxjs';
import {TranslatePipe} from '../../../../shared/pipes/translate.pipe';
import {FileStorageResource} from '../../../../shared/types/generated/fileStorageResource';
import {DialogDocumentsUploadComponent} from '../../../documents/dialog-documents-upload/dialog-documents-upload.component';
import {DocumentsRemoveFileHook, DocumentsUploadFilesHook} from '../../../documents/documents-upload-hooks.type';
import {FeedbackStructureQuestionFormQuestion} from '../../feedback-structure-create-update-dialog/feedback-structure-question/feedback-structure-question-form.model';
import {feedbackAnswerControlName} from '../../helpers/feedback-structure-preview-control-names';
import {FeedbackAttemptPageController} from '../../feedback-attempt/feedback-attempt-page-controller.service';

type FeedbackAttemptFilesQuestion = FeedbackStructureQuestionFormQuestion & {id: number};

@Component({
	selector: 'app-feedback-form-element-files',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [DialogDocumentsUploadComponent, TranslatePipe],
	template: `
		<div class="ps-md-3">
			<app-dialog-documents-upload
				[view]="'component'"
				[files]="selectedFiles()"
				[multiple]="true"
				[type]="'both'"
				[hideMyFilesOption]="true"
				[disabled]="disabled()"
				[uploadFilesFn]="shouldUseAttemptFileHooks() ? uploadAttemptFiles : undefined"
				[removeFileFn]="shouldUseAttemptFileHooks() ? removeAttemptFile : undefined"
				(filesChange)="storeSelectedFiles($event)"
			></app-dialog-documents-upload>
			@if (showRequiredError()) {
				<div class="error-label mt-1" role="alert">
					{{ 'INPUTREQUIRED' | translate }}
				</div>
			}
		</div>
	`,
})
export class FeedbackFormElementFilesComponent {
	private attemptPageController: FeedbackAttemptPageController | null = inject(FeedbackAttemptPageController, {optional: true});

	public question: InputSignal<FeedbackStructureQuestionFormQuestion> = input.required();
	public formGroup: InputSignal<FormGroup> = input.required();
	public controlNamePrefix: InputSignal<string> = input('preview');
	public disabled: InputSignal<boolean> = input(false);

	public readonly uploadAttemptFiles: DocumentsUploadFilesHook = (
		documents: FileList,
		currentFiles: FileStorageResource[]
	): Observable<FileStorageResource[]> => {
		const attemptPageController = this.attemptPageController;
		const question = this.attemptQuestion();
		if (attemptPageController === null || question === null) {
			return of(currentFiles);
		}

		return attemptPageController.uploadQuestionFiles(question, documents, currentFiles);
	};

	public readonly removeAttemptFile: DocumentsRemoveFileHook = (
		file: FileStorageResource,
		currentFiles: FileStorageResource[]
	): Observable<FileStorageResource[]> => {
		const attemptPageController = this.attemptPageController;
		const question = this.attemptQuestion();
		if (attemptPageController === null || question === null) {
			return of(currentFiles);
		}

		return attemptPageController.removeQuestionFile(question, file, currentFiles);
	};

	public selectedFiles(): FileStorageResource[] {
		const value = this.formGroup().controls[this.answerControlName()].value;

		return Array.isArray(value) ? value as FileStorageResource[] : [];
	}

	public storeSelectedFiles(files: FileStorageResource[]): void {
		if (this.disabled()) {
			return;
		}

		this.formGroup().controls[this.answerControlName()].setValue(files);
	}

	public showRequiredError(): boolean {
		const control = this.formGroup().controls[this.answerControlName()];
		return control?.invalid === true && control.dirty;
	}

	public answerControlName(): string {
		return feedbackAnswerControlName(this.question(), this.controlNamePrefix());
	}

	public shouldUseAttemptFileHooks(): boolean {
		return this.attemptQuestion() !== null;
	}

	private attemptQuestion(): FeedbackAttemptFilesQuestion | null {
		const question = this.question();
		if (this.attemptPageController === null || !this.hasQuestionId(question)) {
			return null;
		}

		return question;
	}

	private hasQuestionId(question: FeedbackStructureQuestionFormQuestion): question is FeedbackAttemptFilesQuestion {
		return question.id !== undefined;
	}
}
